import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useFieldArray, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CurrencyYuanOutlined} from '@mui/icons-material';
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {useCreatePayment, useQueryPayment, useUpdatePayment} from "@/service/payment.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, iso2Date} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {PatentPO} from "@/model/product";
import {InputFile, validationFileSchema} from "@/widget/UploadFile/InputFile";
import {WriteContractForm} from "@/pages/Contract/WriteContract";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {SearchMonitorVO} from "@/service/monitor.s";
import {DeleteOutlined, AddOutlined} from '@mui/icons-material';
import {useRoleUsers} from "@/store/user.c";
import {roleAlias} from "@/helper/constant";
import {RoleUserVO} from "@/service/user.s";

const validationSchema = z.object({
    id: z.unknown().default(0),
    instanceId: z.number().default(0),
    executeId: z.unknown(),
    payments: z.array(z.object({
        customerName: z.unknown(),
        customerId: z.number({message: "请选择客户名称"}),
        patentName: z.unknown(),
        patentId: z.number({message: "请选择专利名称"}),
        receivingMethod: z.string({message: "请选择到账方式"}),
        receivingMethodId: z.unknown(),
        feeTypeId: z.number({message: "请选择缴费类型"}),
        feeType: z.unknown(),
        files: z.array(validationFileSchema).optional(),
        arrivalTime: z.date({message: "请选择收款时间"}),
        serviceCharge: z.number({message: '请填写服务费'}).max(1000000, "金额不能大于百万").default(0),
        deadline: z.date({message: "请选择缴费期限"}),
        amount: z.number({message: '请填写缴费金额'}).max(1000000, "金额不能大于百万").default(0),
        remark: z.ostring(),
        handler:
            z.string({message: "请选择处理人"}),
        handlerId:
            z.number({message: "请选择处理人"}),
        tempHandler:
            z.object({username: z.string().default(''), id: z.number()}).optional(),
    }))
});

export type WritePaymentForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WritePayment = () => {
    const {receivingMethod} = useAllDict()
    const {onSearch, id: drawerId, write, execute} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {mutateAsync: createPaymentMutate, isPending: createPaymentPending} = useCreatePayment()
    const {mutateAsync: updatePaymentMutate} = useUpdatePayment()
    const handler = useRoleUsers([roleAlias['processer']])
    const {data: paymentData} = useQueryPayment({id: drawerId!}, checkQueryKey(drawerId))
    const payment = paymentData?.data
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {payments: [{...payment, arrivalTime: iso2Date(payment?.arrivalTime), deadline: iso2Date(payment?.deadline)}]}
        }
        return {payments: [{files: []}]} as any
    }
    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WritePaymentForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WritePaymentForm,
        defaultValues: {...defaultValues, payments: [{files: []}]},
    });
    const {fields, append, remove} = useFieldArray({
        name: "payments",
        control,
        keyName: "_id"
    });

    const onSubmit = async (values: WritePaymentForm) => {
        const result = write == 'add' ? await createPaymentMutate(values) : await updatePaymentMutate({...values, ...execute, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
            setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }
    const watchedValue: any[] = useWatch({
            control,
            name: fields.map((field, index) => `payments.${index}`) as any,
        }
    )

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            {fields.map((field, index) => {
                return <Grid container spacing={3}>
                    <Grid size={6}>
                        <SearchData<WritePaymentForm, CustomerPO> control={control} attr={`payments.${index}.customerName`}
                                                                  attrId={`payments.${index}.customerId`}
                                                                  errMsg={errors?.payments?.[index]?.customerId?.message}
                                                                  setValue={setValue}
                                                                  label={"客户名称"}/>
                    </Grid>
                    <Grid size={6}>
                        <SearchData<WritePaymentForm, PatentPO> control={control} readOnly={!checkQueryKey(watchedValue[index]?.customerId)}
                                                                attr={`payments.${index}.patentName`}
                                                                query={{customerId: watchedValue[index]?.customerId, isMonitor: 1}}
                                                                autoLoad={true}
                                                                tag={8}
                                                                attrId={`payments.${index}.patentId`}
                                                                errMsg={errors?.payments?.[index]?.patentId?.message}
                                                                setValue={setValue}
                                                                label={"专利名称"}/>
                    </Grid>
                    <Grid size={6}>
                        <SearchData<WritePaymentForm, SearchMonitorVO> query={{patentId: watchedValue[index]?.patentId}} readOnly={!checkQueryKey(watchedValue[index]?.patentId)}
                                                                       control={control} attr={`payments.${index}.feeType`}
                                                                       attrId={`payments.${index}.feeTypeId`}
                                                                       errMsg={errors?.payments?.[index]?.feeTypeId?.message}
                                                                       setValue={setValue} autoLoad={true}
                                                                       tag={9}
                                                                       label={"缴费类型"}/>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>缴费金额</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                error={!!errors?.payments?.[index]?.amount}
                                helperText={errors?.payments?.[index]?.amount?.message}
                                type={'number'}
                                value={value}
                                onChange={event => onChange(parseInt(event.target.value))}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            min: 0,
                                            max: 1000000,
                                            maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                        },
                                        endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写缴费金额"
                                variant="outlined"
                            />} name={`payments.${index}.amount`}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>缴费期限</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                <DatePicker
                                    showYearDropdown={true}
                                    showMonthDropdown={true}
                                    dateFormat="yyyy/MM/dd"
                                    selected={value}
                                    onChange={date => onChange(date)}
                                    locale='zhCN'
                                    placeholderText={"选择缴费期限"}
                                    customInput={<TextField
                                        error={!!errors?.payments?.[index]?.deadline}
                                        helperText={errors?.payments?.[index]?.deadline?.message}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                        }}
                                    />}
                                />
                            </DatePickers>} name={`payments.${index}.deadline`}/>
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>服务费</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                error={!!errors?.payments?.[index]?.serviceCharge}
                                helperText={errors?.payments?.[index]?.serviceCharge?.message}
                                type={'number'}
                                value={value}
                                onChange={event => onChange(parseInt(event.target.value))}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            min: 0,
                                            max: 1000000,
                                            maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                        },
                                        endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写服务费"
                                variant="outlined"
                            />} name={`payments.${index}.serviceCharge`}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>收款时间</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                <DatePicker
                                    showYearDropdown={true}
                                    showMonthDropdown={true}
                                    dateFormat="yyyy/MM/dd"
                                    selected={value}
                                    onChange={date => onChange(date)}
                                    locale='zhCN'
                                    placeholderText={"选择收款时间"}
                                    customInput={<TextField
                                        error={!!errors?.payments?.[index]?.arrivalTime}
                                        helperText={errors?.payments?.[index]?.arrivalTime?.message}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                        }}
                                    />}
                                />
                            </DatePickers>} name={`payments.${index}.arrivalTime`}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>到账方式</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                options={receivingMethod}
                                value={value as any || null}
                                onChange={(_, option: DictPO | null) => {
                                    setValue(`payments.${index}.receivingMethodId`, option?.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.payments?.[index]?.receivingMethod}
                                               helperText={errors?.payments?.[index]?.receivingMethod?.message}
                                               variant="outlined"
                                               placeholder={"选择到账方式"} {...params} />
                                )}
                            />} name={`payments.${index}.receivingMethod`}/>
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <InputFile<WriteContractForm> mode={'multiple'} tag={8} isSave={true} targetId={drawerId} label={'附件'} attr={register(`payments.${index}.files`)}
                                                      control={control}
                        />
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>处理人</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    options={handler}
                                    value={value}
                                    onChange={(_, option: RoleUserVO | null) => {
                                        setValue(`payments.${index}.handlerId`, option!.id)
                                        setValue(`payments.${index}.handler`, option!.username)
                                        onChange(option)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id == value.id}
                                    getOptionKey={option => option.username}
                                    getOptionLabel={option => option.username}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField error={!!errors?.payments?.[index]?.handler}
                                                   helperText={errors?.payments?.[index]?.handler?.message}
                                                   variant="outlined"
                                                   placeholder={"选择处理人"} {...params} />
                                    )}
                                />
                            }} name={`payments.${index}.tempHandler`}/>
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth={true}>
                            <FormLabel>备注</FormLabel>
                            <TextField
                                multiline
                                rows={3}
                                {...register(`payments.${index}.remark`)}
                                slotProps={{
                                    input: {
                                        sx: {height: 'auto'},
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="输入备注"
                                fullWidth
                            />
                        </FormControl>
                    </Grid>
                    {write == 'add' &&
                        <Grid size={12} mb={2}>
                            <Stack direction={'row'} justifyContent={'center'} spacing={4}>
                                {fields.length > 1 && <Button size={'small'} startIcon={<DeleteOutlined/>} onClick={() => remove(index)} sx={{width: 100}} color={'error'}
                                                              variant={'outlined'}>删除缴年费</Button>}
                                <Button size={'small'} onClick={() => {
                                    append({files: []} as any)
                                }} startIcon={<AddOutlined/>} color={'primary'} variant={'outlined'}>添加缴年费</Button>
                            </Stack>
                        </Grid>
                    }
                </Grid>
            })}

            <DrawerFooter loading={createPaymentPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
